import React, { useState, useEffect } from 'react';
import {
    Card,
    Row,
    Col,
    Statistic,
    Button,
    Table,
    Tag,
    Space,
    Typography,
    Tabs,
    Form,
    Select,
    DatePicker,
    Input,
    Modal,
    message,
    Spin,
    Alert
} from 'antd';
import {
    CalendarOutlined,
    PlusOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import { attendanceAPI } from '../../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const AttendancePage = () => {
    const [loading, setLoading] = useState(false);
    const [attendanceStatus, setAttendanceStatus] = useState(null);
    const [attendances, setAttendances] = useState([]);
    const [pendingAttendances, setPendingAttendances] = useState([]);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedAttendance, setSelectedAttendance] = useState(null);
    const [approvalLoading, setApprovalLoading] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [form] = Form.useForm();
    const [editForm] = Form.useForm();
    const [rejectForm] = Form.useForm();
    const [activeTab, setActiveTab] = useState('status');

    // 현재 사용자 정보
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

    // 권한 확인
    const userRoles = currentUser.roles ? (
        typeof currentUser.roles === 'string' ? JSON.parse(currentUser.roles) : currentUser.roles
    ) : [];
    const isAdmin = currentUser.role === 'admin' || userRoles.includes('admin');
    const hasApprovalAccess = true; // 모든 사용자에게 탭을 보여주고, API에서 권한 확인

    // 승인 권한 확인
    const canApprove = (attendance) => {
        if (!attendance || !currentUser) {
            console.log('🔍 권한 확인 실패: attendance 또는 currentUser 없음', { attendance, currentUser });
            return false;
        }
        
        // 관리자 권한 확인
        const userRoles = currentUser.roles ? (
            typeof currentUser.roles === 'string' ? JSON.parse(currentUser.roles) : currentUser.roles
        ) : [];
        const isAdmin = currentUser.role === 'admin' || userRoles.includes('admin');
        
        // 부서장 권한 확인 (신청자의 부서장인 경우)
        const isDepartmentHead = attendance.user?.department?.head_id === currentUser.id;
        
        if (selectedAttendance?.status === 'pending') {
            console.log('🔍 권한 확인 정보:', {
                currentUserId: currentUser.id,
                currentUserRole: currentUser.role,
                currentUserRoles: userRoles,
                isAdmin,
                attendanceUserId: attendance.user?.id,
                attendanceUserDept: attendance.user?.department,
                departmentHeadId: attendance.user?.department?.head_id,
                isDepartmentHead,
                canApprove: isAdmin || isDepartmentHead
            });
        }
        
        return isAdmin || isDepartmentHead;
    };

    useEffect(() => {
        fetchAttendanceStatus();
        fetchAttendances();
        
        // 승인 권한이 있으면 승인 대기 목록 로드 (API에서 권한 확인)
        if (hasApprovalAccess) {
            fetchPendingAttendances();
        }
    }, []);

    const fetchAttendanceStatus = async () => {
        try {
            const response = await attendanceAPI.getMyStatus();
            setAttendanceStatus(response.data);
        } catch (error) {
            console.error('Fetch attendance status error:', error);
        }
    };

    const fetchAttendances = async () => {
        setLoading(true);
        try {
            const response = await attendanceAPI.getMy({ limit: 20 });
            setAttendances(response.data.attendances);
        } catch (error) {
            message.error('근태 목록 조회에 실패했습니다');
            console.error('Fetch attendances error:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchPendingAttendances = async () => {
        setLoading(true);
        try {
            const response = await attendanceAPI.getPending({ limit: 20 });
            setPendingAttendances(response.data.attendances);
        } catch (error) {
            console.error('Fetch pending attendances error:', error);
            setPendingAttendances([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateAttendance = async (values) => {
        setCreateLoading(true);
        try {
            const data = {
                ...values,
                start_date: values.date_range ? values.date_range[0].format('YYYY-MM-DD') : values.start_date,
                end_date: values.date_range ? values.date_range[1].format('YYYY-MM-DD') : values.end_date
            };

            // date_range 필드 제거
            delete data.date_range;

            await attendanceAPI.create(data);
            message.success('근태 신청이 완료되었습니다');
            
            form.resetFields();
            setCreateModalVisible(false);
            
            // 데이터 새로고침
            fetchAttendanceStatus();
            fetchAttendances();
        } catch (error) {
            message.error(error.response?.data?.message || '근태 신청에 실패했습니다');
            console.error('Create attendance error:', error);
        } finally {
            setCreateLoading(false);
        }
    };

    // 상세 정보 보기
    const showAttendanceDetail = (attendance) => {
        setSelectedAttendance(attendance);
        setDetailModalVisible(true);
    };

    // 승인 처리
    const handleApprove = async (attendanceId) => {
        setApprovalLoading(true);
        try {
            await attendanceAPI.approve(attendanceId);
            message.success('근태 신청이 승인되었습니다');
            setDetailModalVisible(false);
            fetchAttendances(); // 내 신청 목록 새로고침
            fetchPendingAttendances(); // 승인 대기 목록 새로고침
        } catch (error) {
            message.error(error.response?.data?.message || '승인 처리 중 오류가 발생했습니다');
        } finally {
            setApprovalLoading(false);
        }
    };

    // 반려 처리
    const handleReject = async (values) => {
        setApprovalLoading(true);
        try {
            await attendanceAPI.reject(selectedAttendance.id, { 
                rejection_reason: values.rejection_reason 
            });
            message.success('근태 신청이 반려되었습니다');
            rejectForm.resetFields();
            setDetailModalVisible(false);
            fetchAttendances(); // 내 신청 목록 새로고침
            fetchPendingAttendances(); // 승인 대기 목록 새로고침
        } catch (error) {
            message.error(error.response?.data?.message || '반려 처리 중 오류가 발생했습니다');
        } finally {
            setApprovalLoading(false);
        }
    };

    // 근태 수정 모달 열기
    const handleEditAttendance = (attendance) => {
        setSelectedAttendance(attendance);
        
        // 폼에 기존 데이터 설정
        const startDate = dayjs(attendance.start_date);
        const endDate = dayjs(attendance.end_date);
        
        editForm.setFieldsValue({
            attendance_type: attendance.attendance_type,
            date_range: [startDate, endDate],
            reason: attendance.reason
        });
        
        setEditModalVisible(true);
    };

    // 근태 수정/재상신
    const handleUpdateAttendance = async (values) => {
        setEditLoading(true);
        try {
            const { attendance_type, date_range, reason } = values;
            
            const updateData = {
                attendance_type,
                start_date: date_range[0].format('YYYY-MM-DD'),
                end_date: date_range[1].format('YYYY-MM-DD'),
                reason
            };

            await attendanceAPI.update(selectedAttendance.id, updateData);
            message.success('근태 신청이 수정되어 재상신되었습니다');
            
            setEditModalVisible(false);
            editForm.resetFields();
            fetchAttendances();
            fetchAttendanceStatus();
        } catch (error) {
            message.error(error.response?.data?.message || '근태 수정 중 오류가 발생했습니다');
        } finally {
            setEditLoading(false);
        }
    };

    // 근태 삭제
    const handleDeleteAttendance = (attendance) => {
        Modal.confirm({
            title: '근태 삭제',
            content: `${getAttendanceTypeName(attendance.attendance_type)} 신청을 삭제하시겠습니까?`,
            okText: '삭제',
            okType: 'danger',
            cancelText: '취소',
            onOk: async () => {
                setDeleteLoading(true);
                try {
                    await attendanceAPI.delete(attendance.id);
                    message.success('근태 신청이 삭제되었습니다');
                    fetchAttendances();
                    fetchAttendanceStatus();
                } catch (error) {
                    message.error(error.response?.data?.message || '근태 삭제 중 오류가 발생했습니다');
                } finally {
                    setDeleteLoading(false);
                }
            }
        });
    };

    const attendanceTypeOptions = [
        { value: 'annual_leave', label: '연차' },
        { value: 'sick_leave', label: '병가' },
        { value: 'personal_leave', label: '개인사정' },
        { value: 'bereavement_leave', label: '경조사' },
        { value: 'half_day_morning', label: '오전 반차' },
        { value: 'half_day_afternoon', label: '오후 반차' }
    ];

    const getAttendanceTypeName = (type) => {
        const option = attendanceTypeOptions.find(opt => opt.value === type);
        return option ? option.label : type;
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'orange',
            approved: 'green',
            rejected: 'red'
        };
        return colors[status] || 'default';
    };

    const getStatusText = (status) => {
        const texts = {
            pending: '승인 대기',
            approved: '승인',
            rejected: '반려'
        };
        return texts[status] || status;
    };

    const handleAttendanceTypeChange = (value) => {
        if (value === 'half_day_morning' || value === 'half_day_afternoon') {
            // 반차인 경우 시작일과 종료일을 같게 설정
            form.setFieldsValue({
                date_range: null
            });
        }
    };

    const columns = [
        {
            title: '근태 유형',
            dataIndex: 'attendance_type',
            key: 'attendance_type',
            width: 100,
            render: (type) => (
                <Tag color="blue">{getAttendanceTypeName(type)}</Tag>
            )
        },
        {
            title: '기간',
            key: 'period',
            width: 180,
            render: (_, record) => {
                if (record.start_date === record.end_date) {
                    return dayjs(record.start_date).format('YYYY-MM-DD');
                }
                return `${dayjs(record.start_date).format('YYYY-MM-DD')} ~ ${dayjs(record.end_date).format('YYYY-MM-DD')}`;
            }
        },
        {
            title: '일수',
            dataIndex: 'days_count',
            key: 'days_count',
            width: 70,
            align: 'center',
            render: (days) => `${Number(days).toFixed(1)}일`
        },
        {
            title: '사유',
            dataIndex: 'reason',
            key: 'reason',
            width: 200,
            ellipsis: true,
            render: (reason) => (
                <div title={reason} style={{ 
                    maxWidth: '180px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}>
                    {reason || '-'}
                </div>
            )
        },
        {
            title: '상태',
            dataIndex: 'status',
            key: 'status',
            width: 80,
            align: 'center',
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {getStatusText(status)}
                </Tag>
            )
        },
        {
            title: '신청일',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 140,
            render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
        },
        {
            title: '승인자',
            dataIndex: ['approver', 'name'],
            key: 'approver',
            width: 80,
            render: (name) => name || '-'
        },
        {
            title: '작업',
            key: 'action',
            width: 150,
            render: (_, record) => {
                const canEdit = ['pending', 'rejected'].includes(record.status);
                
                return (
                    <Space>
                        <Button 
                            type="link" 
                            size="small"
                            onClick={() => showAttendanceDetail(record)}
                        >
                            상세보기
                        </Button>
                        {canEdit && (
                            <>
                                <Button 
                                    type="link" 
                                    size="small"
                                    onClick={() => handleEditAttendance(record)}
                                >
                                    수정
                                </Button>
                                <Button 
                                    type="link" 
                                    size="small"
                                    danger
                                    onClick={() => handleDeleteAttendance(record)}
                                >
                                    삭제
                                </Button>
                            </>
                        )}
                    </Space>
                );
            }
        }
    ];

    if (!attendanceStatus) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    const tabItems = [
        {
            key: 'status',
            label: '근태 현황',
            children: (
                <div>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={8} lg={6}>
                            <Card>
                                <Statistic
                                    title="총 연차"
                                    value={attendanceStatus.balance?.total_annual_leave || 15}
                                    precision={1}
                                    prefix={<CalendarOutlined />}
                                    suffix="일"
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={8} lg={6}>
                            <Card>
                                <Statistic
                                    title="사용 연차"
                                    value={attendanceStatus.balance?.used_annual_leave || 0}
                                    precision={1}
                                    prefix={<ClockCircleOutlined />}
                                    suffix="일"
                                    valueStyle={{ color: '#fa8c16' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={8} lg={6}>
                            <Card>
                                <Statistic
                                    title="잔여 연차"
                                    value={attendanceStatus.balance?.remaining_annual_leave || 15}
                                    precision={1}
                                    prefix={<CheckCircleOutlined />}
                                    suffix="일"
                                    valueStyle={{ 
                                        color: (attendanceStatus.balance?.remaining_annual_leave || 15) < 3 ? '#cf1322' : '#52c41a' 
                                    }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={8} lg={6}>
                            <Card>
                                <Statistic
                                    title="이월/포상 연차"
                                    value={
                                        (attendanceStatus.balance?.carry_over_days || 0) + 
                                        (attendanceStatus.balance?.bonus_days || 0)
                                    }
                                    precision={1}
                                    prefix={<ExclamationCircleOutlined />}
                                    suffix="일"
                                    valueStyle={{ color: '#722ed1' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {attendanceStatus.recent_attendances && attendanceStatus.recent_attendances.length > 0 && (
                        <Card title="최근 근태 신청" style={{ marginTop: 24 }}>
                            <Table
                                dataSource={attendanceStatus.recent_attendances}
                                columns={columns.slice(0, -2)} // 승인자, 작업 컬럼 제외
                                rowKey="id"
                                pagination={false}
                                size="small"
                                scroll={{ x: 800 }}
                            />
                        </Card>
                    )}
                </div>
            )
        },
        {
            key: 'list',
            label: '신청 내역',
            children: (
                <Card
                    title="근태 신청 내역"
                    extra={
                        <Button 
                            type="primary" 
                            icon={<PlusOutlined />}
                            onClick={() => setCreateModalVisible(true)}
                        >
                            근태 신청
                        </Button>
                    }
                >
                    <Table
                        loading={loading}
                        dataSource={attendances}
                        columns={columns}
                        rowKey="id"
                        pagination={{ pageSize: 10 }}
                        scroll={{ x: 1000 }}
                        expandable={{
                            expandedRowRender: (record) => (
                                <div style={{ padding: '12px', background: '#fafafa' }}>
                                    {record.rejection_reason && (
                                        <div style={{ color: '#cf1322' }}>
                                            <strong>반려 사유:</strong> {record.rejection_reason}
                                        </div>
                                    )}
                                    {record.emergency_contact && (
                                        <div style={{ marginTop: 8, color: '#666' }}>
                                            <strong>긴급 연락처:</strong> {record.emergency_contact}
                                        </div>
                                    )}
                                </div>
                            )
                        }}
                    />
                </Card>
            )
        }
    ];

    // 승인 대기 탭 추가 (관리자나 부서장인 경우)
    if (hasApprovalAccess) {
        tabItems.push({
            key: 'pending',
            label: `승인 대기 (${pendingAttendances.length})`,
            children: (
                <Card>
                    <Table
                        loading={loading}
                        dataSource={pendingAttendances}
                        columns={columns}
                        rowKey="id"
                        pagination={{ pageSize: 10 }}
                        scroll={{ x: 1000 }}
                        expandable={{
                            expandedRowRender: (record) => (
                                <div style={{ padding: '12px', background: '#fafafa' }}>
                                    {record.rejection_reason && (
                                        <div style={{ color: '#cf1322' }}>
                                            <strong>반려 사유:</strong> {record.rejection_reason}
                                        </div>
                                    )}
                                    {record.emergency_contact && (
                                        <div style={{ marginTop: 8, color: '#666' }}>
                                            <strong>긴급 연락처:</strong> {record.emergency_contact}
                                        </div>
                                    )}
                                </div>
                            )
                        }}
                    />
                </Card>
            )
        });
    }

    return (
        <div>
            <Title level={2}>근태 관리</Title>

            <Tabs
                activeKey={activeTab}
                onChange={(tab) => {
                    setActiveTab(tab);
                    if (tab === 'pending') {
                        fetchPendingAttendances();
                    }
                }}
                items={tabItems}
                tabBarExtraContent={
                    activeTab === 'status' && (
                        <Button 
                            type="primary" 
                            icon={<PlusOutlined />}
                            onClick={() => setCreateModalVisible(true)}
                        >
                            근태 신청
                        </Button>
                    )
                }
            />

            {/* 근태 신청 모달 */}
            <Modal
                title="근태 신청"
                open={createModalVisible}
                onCancel={() => {
                    form.resetFields();
                    setCreateModalVisible(false);
                }}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreateAttendance}
                >
                    <Form.Item
                        name="attendance_type"
                        label="근태 유형"
                        rules={[{ required: true, message: '근태 유형을 선택하세요' }]}
                    >
                        <Select 
                            placeholder="근태 유형을 선택하세요"
                            onChange={handleAttendanceTypeChange}
                        >
                            {attendanceTypeOptions.map(option => (
                                <Option key={option.value} value={option.value}>
                                    {option.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => 
                            prevValues.attendance_type !== currentValues.attendance_type
                        }
                    >
                        {({ getFieldValue }) => {
                            const attendanceType = getFieldValue('attendance_type');
                            const isHalfDay = attendanceType === 'half_day_morning' || attendanceType === 'half_day_afternoon';

                            return isHalfDay ? (
                                <Form.Item
                                    name="start_date"
                                    label="날짜"
                                    rules={[{ required: true, message: '날짜를 선택하세요' }]}
                                >
                                    <DatePicker style={{ width: '100%' }} />
                                </Form.Item>
                            ) : (
                                <Form.Item
                                    name="date_range"
                                    label="기간"
                                    rules={[{ required: true, message: '기간을 선택하세요' }]}
                                >
                                    <RangePicker style={{ width: '100%' }} />
                                </Form.Item>
                            );
                        }}
                    </Form.Item>

                    <Form.Item
                        name="reason"
                        label="사유"
                        rules={[{ required: true, message: '사유를 입력하세요' }]}
                    >
                        <TextArea 
                            rows={3} 
                            placeholder="근태 신청 사유를 입력하세요"
                        />
                    </Form.Item>

                    <Form.Item
                        name="emergency_contact"
                        label="긴급 연락처"
                    >
                        <Input placeholder="장기 휴가 시 긴급 연락처 (선택사항)" />
                    </Form.Item>

                    <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
                        <Space>
                            <Button onClick={() => {
                                form.resetFields();
                                setCreateModalVisible(false);
                            }}>
                                취소
                            </Button>
                            <Button 
                                type="primary" 
                                htmlType="submit"
                                loading={createLoading}
                            >
                                신청
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 근태 상세 모달 */}
            <Modal
                title="근태 신청 상세"
                open={detailModalVisible}
                onCancel={() => {
                    setDetailModalVisible(false);
                    setSelectedAttendance(null);
                    rejectForm.resetFields();
                }}
                footer={null}
                width={700}
            >
                {selectedAttendance && (
                    <div>
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <strong>신청자:</strong> {selectedAttendance.user?.name || '알 수 없음'}
                            </Col>
                            <Col span={12}>
                                <strong>근태 유형:</strong> 
                                <Tag color="blue" style={{ marginLeft: 8 }}>
                                    {getAttendanceTypeName(selectedAttendance.attendance_type)}
                                </Tag>
                            </Col>
                            <Col span={12}>
                                <strong>기간:</strong> 
                                {selectedAttendance.start_date === selectedAttendance.end_date 
                                    ? dayjs(selectedAttendance.start_date).format('YYYY-MM-DD')
                                    : `${dayjs(selectedAttendance.start_date).format('YYYY-MM-DD')} ~ ${dayjs(selectedAttendance.end_date).format('YYYY-MM-DD')}`
                                }
                            </Col>
                            <Col span={12}>
                                <strong>일수:</strong> {Number(selectedAttendance.days_count).toFixed(1)}일
                            </Col>
                            <Col span={12}>
                                <strong>상태:</strong> 
                                <Tag color={getStatusColor(selectedAttendance.status)} style={{ marginLeft: 8 }}>
                                    {getStatusText(selectedAttendance.status)}
                                </Tag>
                            </Col>
                            <Col span={12}>
                                <strong>신청일:</strong> {dayjs(selectedAttendance.created_at).format('YYYY-MM-DD HH:mm')}
                            </Col>
                            {selectedAttendance.reason && (
                                <Col span={24}>
                                    <strong>사유:</strong>
                                    <div style={{ marginTop: 8, padding: '12px', background: '#fafafa', borderRadius: '4px' }}>
                                        {selectedAttendance.reason}
                                    </div>
                                </Col>
                            )}
                            {selectedAttendance.rejection_reason && (
                                <Col span={24}>
                                    <strong>반려 사유:</strong>
                                    <div style={{ marginTop: 8, padding: '12px', background: '#fff2f0', borderRadius: '4px', border: '1px solid #ffccc7' }}>
                                        {selectedAttendance.rejection_reason}
                                    </div>
                                </Col>
                            )}
                            {selectedAttendance.approved_at && (
                                <Col span={12}>
                                    <strong>승인일:</strong> {dayjs(selectedAttendance.approved_at).format('YYYY-MM-DD HH:mm')}
                                </Col>
                            )}
                            {selectedAttendance.approver?.name && (
                                <Col span={12}>
                                    <strong>승인자:</strong> {selectedAttendance.approver.name}
                                </Col>
                            )}
                        </Row>

                        {/* 승인/반려 버튼 (신청중이고 승인 권한이 있는 경우에만 표시) */}
                        {selectedAttendance.status === 'pending' && canApprove(selectedAttendance) && (
                            <div style={{ marginTop: 24, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                                <Form
                                    form={rejectForm}
                                    onFinish={handleReject}
                                    layout="vertical"
                                >
                                    <Row gutter={16}>
                                        <Col span={24}>
                                            <Space style={{ width: '100%', justifyContent: 'center' }}>
                                                <Button 
                                                    type="primary"
                                                    onClick={() => handleApprove(selectedAttendance.id)}
                                                    loading={approvalLoading}
                                                    icon={<CheckCircleOutlined />}
                                                >
                                                    승인
                                                </Button>
                                                <Button 
                                                    danger
                                                    onClick={() => {
                                                        Modal.confirm({
                                                            title: '반려 처리',
                                                            content: (
                                                                <div>
                                                                    <p>이 근태 신청을 반려하시겠습니까?</p>
                                                                    <Form
                                                                        form={rejectForm}
                                                                        layout="vertical"
                                                                    >
                                                                        <Form.Item
                                                                            name="rejection_reason"
                                                                            label="반려 사유"
                                                                            rules={[{ required: true, message: '반려 사유를 입력해주세요' }]}
                                                                        >
                                                                            <TextArea
                                                                                rows={3}
                                                                                placeholder="반려 사유를 입력해주세요"
                                                                            />
                                                                        </Form.Item>
                                                                    </Form>
                                                                </div>
                                                            ),
                                                            onOk: () => {
                                                                rejectForm.submit();
                                                            },
                                                            okText: '반려',
                                                            cancelText: '취소',
                                                            okType: 'danger'
                                                        });
                                                    }}
                                                    icon={<ExclamationCircleOutlined />}
                                                >
                                                    반려
                                                </Button>
                                            </Space>
                                        </Col>
                                    </Row>
                                </Form>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            {/* 근태 수정 모달 */}
            <Modal
                title="근태 수정"
                open={editModalVisible}
                onOk={() => editForm.submit()}
                onCancel={() => {
                    setEditModalVisible(false);
                    editForm.resetFields();
                    setSelectedAttendance(null);
                }}
                okText="수정"
                cancelText="취소"
                confirmLoading={editLoading}
                width={600}
            >
                <Form
                    form={editForm}
                    layout="vertical"
                    onFinish={handleUpdateAttendance}
                >
                    <Form.Item
                        name="attendance_type"
                        label="근태 유형"
                        rules={[{ required: true, message: '근태 유형을 선택하세요' }]}
                    >
                        <Select
                            placeholder="근태 유형을 선택하세요"
                            options={attendanceTypeOptions}
                            onChange={handleAttendanceTypeChange}
                        />
                    </Form.Item>

                    <Form.Item
                        name="date_range"
                        label="기간"
                        rules={[{ required: true, message: '기간을 선택하세요' }]}
                    >
                        <RangePicker
                            style={{ width: '100%' }}
                            placeholder={['시작일', '종료일']}
                            format="YYYY-MM-DD"
                            disabledDate={(current) => {
                                return current && current < dayjs().startOf('day');
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="reason"
                        label="사유"
                    >
                        <TextArea
                            rows={3}
                            placeholder="근태 신청 사유를 입력하세요"
                        />
                    </Form.Item>

                    <Alert
                        message="안내사항"
                        description="수정된 근태는 다시 승인 대기 상태로 변경되어 재승인이 필요합니다."
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                </Form>
            </Modal>
        </div>
    );
};

export default AttendancePage;